import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "../cn.ts";

export const DropdownMenu = Dropdown.Root;
export const DropdownMenuTrigger = Dropdown.Trigger;
export const DropdownMenuGroup = Dropdown.Group;
export const DropdownMenuPortal = Dropdown.Portal;
export const DropdownMenuSub = Dropdown.Sub;
export const DropdownMenuRadioGroup = Dropdown.RadioGroup;

const itemClass =
  "relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-[var(--brand-pink-soft)] data-[highlighted]:text-[var(--brand-ink)]";

export const DropdownMenuContent = forwardRef<
  React.ElementRef<typeof Dropdown.Content>,
  React.ComponentPropsWithoutRef<typeof Dropdown.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, ...rest }, ref) {
  return (
    <Dropdown.Portal>
      <Dropdown.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-50 min-w-[10rem] overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-paper)] p-1 shadow-md",
          "motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out",
          className,
        )}
        {...rest}
      />
    </Dropdown.Portal>
  );
});

export const DropdownMenuItem = forwardRef<
  React.ElementRef<typeof Dropdown.Item>,
  React.ComponentPropsWithoutRef<typeof Dropdown.Item>
>(function DropdownMenuItem({ className, ...rest }, ref) {
  return <Dropdown.Item ref={ref} className={cn(itemClass, className)} {...rest} />;
});

export const DropdownMenuLabel = forwardRef<
  React.ElementRef<typeof Dropdown.Label>,
  React.ComponentPropsWithoutRef<typeof Dropdown.Label>
>(function DropdownMenuLabel({ className, ...rest }, ref) {
  return (
    <Dropdown.Label
      ref={ref}
      className={cn("px-2 py-1.5 text-xs font-semibold text-[var(--brand-muted)]", className)}
      {...rest}
    />
  );
});

export const DropdownMenuSeparator = forwardRef<
  React.ElementRef<typeof Dropdown.Separator>,
  React.ComponentPropsWithoutRef<typeof Dropdown.Separator>
>(function DropdownMenuSeparator({ className, ...rest }, ref) {
  return (
    <Dropdown.Separator
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-[var(--brand-border)]", className)}
      {...rest}
    />
  );
});

export const DropdownMenuCheckboxItem = forwardRef<
  React.ElementRef<typeof Dropdown.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof Dropdown.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, checked, ...rest }, ref) {
  return (
    <Dropdown.CheckboxItem
      ref={ref}
      checked={checked}
      className={cn(itemClass, "pl-8", className)}
      {...rest}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <Dropdown.ItemIndicator>
          <Check className="h-3.5 w-3.5" />
        </Dropdown.ItemIndicator>
      </span>
      {children}
    </Dropdown.CheckboxItem>
  );
});

export const DropdownMenuRadioItem = forwardRef<
  React.ElementRef<typeof Dropdown.RadioItem>,
  React.ComponentPropsWithoutRef<typeof Dropdown.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...rest }, ref) {
  return (
    <Dropdown.RadioItem ref={ref} className={cn(itemClass, "pl-8", className)} {...rest}>
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <Dropdown.ItemIndicator>
          <Circle className="h-2 w-2 fill-current" />
        </Dropdown.ItemIndicator>
      </span>
      {children}
    </Dropdown.RadioItem>
  );
});

export const DropdownMenuSubTrigger = forwardRef<
  React.ElementRef<typeof Dropdown.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof Dropdown.SubTrigger>
>(function DropdownMenuSubTrigger({ className, children, ...rest }, ref) {
  return (
    <Dropdown.SubTrigger ref={ref} className={cn(itemClass, className)} {...rest}>
      {children}
      <ChevronRight className="ml-auto h-4 w-4" />
    </Dropdown.SubTrigger>
  );
});

export const DropdownMenuSubContent = forwardRef<
  React.ElementRef<typeof Dropdown.SubContent>,
  React.ComponentPropsWithoutRef<typeof Dropdown.SubContent>
>(function DropdownMenuSubContent({ className, ...rest }, ref) {
  return (
    <Dropdown.SubContent
      ref={ref}
      className={cn(
        "z-50 min-w-[10rem] overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-paper)] p-1 shadow-md",
        className,
      )}
      {...rest}
    />
  );
});
